/**
 * Alignment Engine — positions rects inside zones; no drawing, no styling.
 *
 * All functions are pure: rect in, rect out. The composition engine uses
 * alignInZone() for every placed object so alignment is decided in exactly
 * one place, which is also what makes the validation engine's alignment
 * checks meaningful.
 */

export const H_ALIGN = ['left', 'center', 'right'];
export const V_ALIGN = ['top', 'middle', 'bottom'];

/**
 * Place a box of { width, height } inside `zone` according to alignment.
 * Returns a full rect. Oversized boxes are clamped to the zone.
 */
export const alignInZone = (box, zone, { horizontal = 'center', vertical = 'top' } = {}) => {
  const width = Math.min(box.width, zone.width);
  const height = Math.min(box.height, zone.height);

  let x = zone.x;
  if (horizontal === 'center') x = zone.x + (zone.width - width) / 2;
  else if (horizontal === 'right') x = zone.x + zone.width - width;

  let y = zone.y;
  if (vertical === 'middle') y = zone.y + (zone.height - height) / 2;
  else if (vertical === 'bottom') y = zone.y + zone.height - height;

  return { x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
};

/** Snap a rect's position to a grid step. */
export const snapToGrid = (rect, step = 4) => ({
  ...rect,
  x: Math.round(rect.x / step) * step,
  y: Math.round(rect.y / step) * step,
});

/**
 * Measure how consistently a set of rects is aligned: the share of rects
 * whose left edge or horizontal center matches another rect's (±tolerance).
 * Used by the quality engine; 1 = perfectly disciplined layout.
 */
export const alignmentConsistency = (rects, tolerance = 4) => {
  if (rects.length < 2) return 1;
  const lefts = rects.map((r) => r.x);
  const centers = rects.map((r) => r.x + r.width / 2);
  const matches = rects.filter((_, i) => {
    const sharesEdge = lefts.some((v, j) => j !== i && Math.abs(v - lefts[i]) <= tolerance);
    const sharesCenter = centers.some((v, j) => j !== i && Math.abs(v - centers[i]) <= tolerance);
    return sharesEdge || sharesCenter;
  });
  return matches.length / rects.length;
};
